import { useEffect, useState, useCallback } from 'react';
import { Copy, RefreshCw } from 'lucide-react';
import { useCanvasStore } from '../../stores/canvasStore';
import { useStudioStore } from '../../stores/studioStore';

interface ChildElementInfo {
  id: string;
  name: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface GroupPositioningReading {
  elementId: string;
  elementName: string;
  storeX: number;
  storeY: number;
  storeWidth: number;
  storeHeight: number;
  storeRotation: number;
  childIdsCount: number;

  children: ChildElementInfo[];
  childrenBoundingBox: { minX: number; minY: number; maxX: number; maxY: number; width: number; height: number } | null;
  deltaStoreVsChildBounds: { dx: number; dy: number; dw: number; dh: number } | null;

  fabricLeft: number;
  fabricTop: number;
  fabricWidth: number;
  fabricHeight: number;
  fabricScaleX: number;
  fabricScaleY: number;
  fabricOriginX: string;
  fabricOriginY: string;
  fabricAngle: number;

  artboardLeft: number;
  artboardTop: number;
  artboardWidth: number;
  artboardHeight: number;

  expectedFabricLeft: number;
  expectedFabricTop: number;
  deltaFabricPosition: { dLeft: number; dTop: number };

  isOnCanvas: boolean;
  offCanvasReason: string | null;

  verdict: string;
  verdictSeverity: 'ok' | 'warning' | 'error';
}

const ELEMENT_ID_KEY = '_csElementId';

function readGroupDiagnostic(): GroupPositioningReading | null {
  const fc = useCanvasStore.getState().fabricCanvas;
  if (!fc) return null;
  const active = fc.getActiveObject();
  if (!active) return null;

  const elementId = (active as unknown as Record<string, unknown>)[ELEMENT_ID_KEY] as string | undefined;
  if (!elementId) return null;

  const storeState = useStudioStore.getState();
  const storeEl = storeState.canvasState.elements.find((e) => e.id === elementId);
  if (!storeEl || storeEl.type !== 'group') return null;

  const groupEl = storeEl as typeof storeEl & {
    x: number;
    y: number;
    width: number;
    height: number;
    rotation?: number;
    childIds?: string[];
    name?: string;
  };

  const childIds = groupEl.childIds ?? [];
  const allElements = storeState.canvasState.elements;
  const children: ChildElementInfo[] = childIds
    .map((cid) => {
      const child = allElements.find((e) => e.id === cid) as (typeof allElements)[number] & {
        x: number;
        y: number;
        width: number;
        height: number;
        name?: string;
      } | undefined;
      if (!child) return null;
      return {
        id: child.id,
        name: child.name ?? '(unnamed)',
        type: child.type,
        x: child.x,
        y: child.y,
        width: child.width,
        height: child.height,
      };
    })
    .filter((c): c is ChildElementInfo => c !== null);

  let childrenBoundingBox: GroupPositioningReading['childrenBoundingBox'] = null;
  if (children.length > 0) {
    const minX = Math.min(...children.map((c) => c.x));
    const minY = Math.min(...children.map((c) => c.y));
    const maxX = Math.max(...children.map((c) => c.x + c.width));
    const maxY = Math.max(...children.map((c) => c.y + c.height));
    childrenBoundingBox = { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
  }

  const deltaStoreVsChildBounds = childrenBoundingBox
    ? {
        dx: groupEl.x - childrenBoundingBox.minX,
        dy: groupEl.y - childrenBoundingBox.minY,
        dw: groupEl.width - childrenBoundingBox.width,
        dh: groupEl.height - childrenBoundingBox.height,
      }
    : null;

  const fabricLeft = (active as unknown as { left?: number }).left ?? 0;
  const fabricTop = (active as unknown as { top?: number }).top ?? 0;
  const fabricWidth = (active as unknown as { width?: number }).width ?? 0;
  const fabricHeight = (active as unknown as { height?: number }).height ?? 0;
  const fabricScaleX = (active as unknown as { scaleX?: number }).scaleX ?? 1;
  const fabricScaleY = (active as unknown as { scaleY?: number }).scaleY ?? 1;
  const fabricOriginX = String((active as unknown as { originX?: string }).originX ?? 'left');
  const fabricOriginY = String((active as unknown as { originY?: string }).originY ?? 'top');
  const fabricAngle = (active as unknown as { angle?: number }).angle ?? 0;

  const artboardObj = fc.getObjects().find(
    (o: unknown) => (o as { _csIsArtboard?: boolean })._csIsArtboard === true
  ) as { left?: number; top?: number; width?: number; height?: number } | undefined;
  const artboardLeft = artboardObj?.left ?? 0;
  const artboardTop = artboardObj?.top ?? 0;
  const artboardWidth = artboardObj?.width ?? 0;
  const artboardHeight = artboardObj?.height ?? 0;

  const expectedFabricLeft = artboardLeft + groupEl.x;
  const expectedFabricTop = artboardTop + groupEl.y;
  const deltaFabricPosition = {
    dLeft: fabricLeft - expectedFabricLeft,
    dTop: fabricTop - expectedFabricTop,
  };

  const artboardRight = artboardLeft + artboardWidth;
  const artboardBottom = artboardTop + artboardHeight;
  const groupRight = fabricLeft + fabricWidth * fabricScaleX;
  const groupBottom = fabricTop + fabricHeight * fabricScaleY;

  let isOnCanvas = true;
  let offCanvasReason: string | null = null;
  if (fabricLeft > artboardRight) {
    isOnCanvas = false;
    offCanvasReason = `left (${fabricLeft.toFixed(0)}) > artboard right (${artboardRight.toFixed(0)})`;
  } else if (fabricTop > artboardBottom) {
    isOnCanvas = false;
    offCanvasReason = `top (${fabricTop.toFixed(0)}) > artboard bottom (${artboardBottom.toFixed(0)})`;
  } else if (groupRight < artboardLeft) {
    isOnCanvas = false;
    offCanvasReason = `right (${groupRight.toFixed(0)}) < artboard left (${artboardLeft.toFixed(0)})`;
  } else if (groupBottom < artboardTop) {
    isOnCanvas = false;
    offCanvasReason = `bottom (${groupBottom.toFixed(0)}) < artboard top (${artboardTop.toFixed(0)})`;
  }

  let verdict = '';
  let verdictSeverity: 'ok' | 'warning' | 'error' = 'ok';

  if (deltaStoreVsChildBounds && (Math.abs(deltaStoreVsChildBounds.dx) > 1 || Math.abs(deltaStoreVsChildBounds.dy) > 1)) {
    verdict = `Bug: store x/y do not match child bounding box (dx=${deltaStoreVsChildBounds.dx.toFixed(1)}, dy=${deltaStoreVsChildBounds.dy.toFixed(1)})`;
    verdictSeverity = 'error';
  } else if (Math.abs(deltaFabricPosition.dLeft) > 1 || Math.abs(deltaFabricPosition.dTop) > 1) {
    verdict = `Bug: Fabric position deviates from expected (dLeft=${deltaFabricPosition.dLeft.toFixed(1)}, dTop=${deltaFabricPosition.dTop.toFixed(1)})`;
    verdictSeverity = 'error';
  } else if (!isOnCanvas) {
    verdict = `Bug: group is off-canvas (${offCanvasReason})`;
    verdictSeverity = 'error';
  } else {
    verdict = 'OK: store, factory, and sync all consistent -- group is on-canvas';
    verdictSeverity = 'ok';
  }

  return {
    elementId,
    elementName: groupEl.name ?? '(unnamed)',
    storeX: groupEl.x,
    storeY: groupEl.y,
    storeWidth: groupEl.width,
    storeHeight: groupEl.height,
    storeRotation: groupEl.rotation ?? 0,
    childIdsCount: childIds.length,

    children,
    childrenBoundingBox,
    deltaStoreVsChildBounds,

    fabricLeft,
    fabricTop,
    fabricWidth,
    fabricHeight,
    fabricScaleX,
    fabricScaleY,
    fabricOriginX,
    fabricOriginY,
    fabricAngle,

    artboardLeft,
    artboardTop,
    artboardWidth,
    artboardHeight,

    expectedFabricLeft,
    expectedFabricTop,
    deltaFabricPosition,

    isOnCanvas,
    offCanvasReason,

    verdict,
    verdictSeverity,
  };
}

export function GroupPositioningDiagnostic() {
  const [reading, setReading] = useState<GroupPositioningReading | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const fabricCanvas = useCanvasStore((s) => s.fabricCanvas);

  const refresh = useCallback(() => {
    setReading(readGroupDiagnostic());
  }, []);

  useEffect(() => {
    if (!fabricCanvas) return;

    const handleSelection = () => {
      requestAnimationFrame(() => refresh());
    };
    const handleCleared = () => setReading(null);

    fabricCanvas.on('selection:created', handleSelection);
    fabricCanvas.on('selection:updated', handleSelection);
    fabricCanvas.on('selection:cleared', handleCleared);

    refresh();

    return () => {
      fabricCanvas.off('selection:created', handleSelection);
      fabricCanvas.off('selection:updated', handleSelection);
      fabricCanvas.off('selection:cleared', handleCleared);
    };
  }, [fabricCanvas, refresh]);

  const handleCopy = useCallback(async () => {
    if (!reading) return;
    const payload = {
      capturedAt: new Date().toISOString(),
      panel: 'GroupPositioningDiagnostic',
      reading,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1500);
    } catch {
      // ignore
    }
  }, [reading]);

  if (!reading) {
    return (
      <div style={emptyStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontWeight: 600, color: '#fbbf24' }}>Group Positioning Diagnostic</span>
        </div>
        <div style={{ color: '#9ca3af' }}>Select a group element on the canvas to inspect its positioning.</div>
      </div>
    );
  }

  const verdictColor =
    reading.verdictSeverity === 'ok' ? '#4ade80' : reading.verdictSeverity === 'warning' ? '#fbbf24' : '#ef4444';

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontWeight: 700, color: '#fbbf24' }}>Group Positioning Diagnostic</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={refresh} title="Refresh" style={btnStyle}>
            <RefreshCw size={12} />
          </button>
          <button
            onClick={handleCopy}
            title="Copy as JSON"
            style={{
              ...btnStyle,
              background: copyState === 'copied' ? 'rgba(34, 197, 94, 0.2)' : 'transparent',
              color: copyState === 'copied' ? '#4ade80' : '#9ca3af',
            }}
          >
            <Copy size={12} />
            <span style={{ fontSize: 10, marginLeft: 4 }}>{copyState === 'copied' ? 'COPIED' : 'COPY'}</span>
          </button>
        </div>
      </div>

      <div style={verdictBoxStyle(verdictColor)}>
        <span style={{ color: verdictColor, fontWeight: 700 }}>VERDICT: </span>
        <span style={{ color: '#e5e7eb' }}>{reading.verdict}</span>
      </div>

      <SectionHeader>Group Element Store State</SectionHeader>
      <Row label="id" value={reading.elementId.slice(0, 8) + '...'} />
      <Row label="name" value={reading.elementName} />
      <Row label="storeX" value={reading.storeX.toFixed(2)} />
      <Row label="storeY" value={reading.storeY.toFixed(2)} />
      <Row label="storeWidth" value={reading.storeWidth.toFixed(2)} />
      <Row label="storeHeight" value={reading.storeHeight.toFixed(2)} />
      <Row label="storeRotation" value={reading.storeRotation} />
      <Row label="childIds count" value={reading.childIdsCount} />

      <SectionHeader>Children Bounding Box</SectionHeader>
      {reading.childrenBoundingBox ? (
        <>
          <Row label="minX" value={reading.childrenBoundingBox.minX.toFixed(2)} />
          <Row label="minY" value={reading.childrenBoundingBox.minY.toFixed(2)} />
          <Row label="maxX" value={reading.childrenBoundingBox.maxX.toFixed(2)} />
          <Row label="maxY" value={reading.childrenBoundingBox.maxY.toFixed(2)} />
          <Row label="width" value={reading.childrenBoundingBox.width.toFixed(2)} />
          <Row label="height" value={reading.childrenBoundingBox.height.toFixed(2)} />
        </>
      ) : (
        <div style={{ color: '#9ca3af', fontSize: 10, padding: '4px 0' }}>(no children resolved)</div>
      )}

      {reading.deltaStoreVsChildBounds && (
        <>
          <SectionHeader>Store vs Child Bounds Delta</SectionHeader>
          <Row
            label="dx (storeX - minX)"
            value={reading.deltaStoreVsChildBounds.dx.toFixed(2)}
            highlight={Math.abs(reading.deltaStoreVsChildBounds.dx) > 1}
          />
          <Row
            label="dy (storeY - minY)"
            value={reading.deltaStoreVsChildBounds.dy.toFixed(2)}
            highlight={Math.abs(reading.deltaStoreVsChildBounds.dy) > 1}
          />
          <Row
            label="dw (storeW - childW)"
            value={reading.deltaStoreVsChildBounds.dw.toFixed(2)}
            highlight={Math.abs(reading.deltaStoreVsChildBounds.dw) > 1}
          />
          <Row
            label="dh (storeH - childH)"
            value={reading.deltaStoreVsChildBounds.dh.toFixed(2)}
            highlight={Math.abs(reading.deltaStoreVsChildBounds.dh) > 1}
          />
        </>
      )}

      <SectionHeader>Child Elements</SectionHeader>
      {reading.children.length === 0 ? (
        <div style={{ color: '#9ca3af', fontSize: 10, padding: '4px 0' }}>(none)</div>
      ) : (
        reading.children.map((c, i) => (
          <div key={i} style={{ padding: '3px 0', borderBottom: '1px dotted rgba(255,255,255,0.06)', fontSize: 10 }}>
            <div style={{ color: '#fbbf24' }}>
              {c.name} <span style={{ color: '#9ca3af' }}>({c.type})</span>
            </div>
            <div style={{ color: '#e5e7eb', paddingLeft: 8 }}>
              x={c.x.toFixed(1)}, y={c.y.toFixed(1)}, w={c.width.toFixed(1)}, h={c.height.toFixed(1)}
            </div>
          </div>
        ))
      )}

      <SectionHeader>Fabric Object State</SectionHeader>
      <Row label="fabricLeft" value={reading.fabricLeft.toFixed(2)} />
      <Row label="fabricTop" value={reading.fabricTop.toFixed(2)} />
      <Row label="fabricWidth" value={reading.fabricWidth.toFixed(2)} />
      <Row label="fabricHeight" value={reading.fabricHeight.toFixed(2)} />
      <Row label="scaleX" value={reading.fabricScaleX} />
      <Row label="scaleY" value={reading.fabricScaleY} />
      <Row label="originX" value={reading.fabricOriginX} />
      <Row label="originY" value={reading.fabricOriginY} />
      <Row label="angle" value={reading.fabricAngle} />

      <SectionHeader>Artboard Context</SectionHeader>
      <Row label="artboardLeft" value={reading.artboardLeft.toFixed(2)} />
      <Row label="artboardTop" value={reading.artboardTop.toFixed(2)} />
      <Row label="artboardWidth" value={reading.artboardWidth.toFixed(2)} />
      <Row label="artboardHeight" value={reading.artboardHeight.toFixed(2)} />

      <SectionHeader>Expected vs Actual Position</SectionHeader>
      <Row label="expectedFabricLeft" value={reading.expectedFabricLeft.toFixed(2)} />
      <Row label="expectedFabricTop" value={reading.expectedFabricTop.toFixed(2)} />
      <Row
        label="dLeft (fabric - expected)"
        value={reading.deltaFabricPosition.dLeft.toFixed(2)}
        highlight={Math.abs(reading.deltaFabricPosition.dLeft) > 1}
      />
      <Row
        label="dTop (fabric - expected)"
        value={reading.deltaFabricPosition.dTop.toFixed(2)}
        highlight={Math.abs(reading.deltaFabricPosition.dTop) > 1}
      />

      <SectionHeader>Visual Position Analysis</SectionHeader>
      <Row label="on-canvas" value={reading.isOnCanvas} highlight={!reading.isOnCanvas} />
      {reading.offCanvasReason && <Row label="off-canvas reason" value={reading.offCanvasReason} highlight />}
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  background: 'rgba(15, 15, 18, 0.97)',
  color: '#e5e7eb',
  border: '1px solid rgba(255, 255, 255, 0.14)',
  borderRadius: 10,
  padding: '12px 14px',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 11,
  maxHeight: '70vh',
  overflowY: 'auto',
};

const emptyStyle: React.CSSProperties = {
  background: 'rgba(20, 20, 24, 0.96)',
  color: '#e5e7eb',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  borderRadius: 8,
  padding: '10px 14px',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 11,
};

const btnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.15)',
  color: '#9ca3af',
  cursor: 'pointer',
  padding: '2px 6px',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
};

const verdictBoxStyle = (color: string): React.CSSProperties => ({
  border: `1px solid ${color}`,
  background: `${color}15`,
  padding: '6px 8px',
  borderRadius: 4,
  marginBottom: 8,
  fontSize: 11,
  lineHeight: 1.4,
});

function SectionHeader({ children }: { children: string }) {
  return (
    <div style={{ marginTop: 10, marginBottom: 4, color: '#fbbf24', fontWeight: 700, fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase' }}>
      {children}
    </div>
  );
}

function Row({ label, value, highlight }: { label: string; value: string | number | boolean | null | undefined; highlight?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px dotted rgba(255,255,255,0.06)' }}>
      <span style={{ color: '#9ca3af' }}>{label}</span>
      <span
        style={{
          color: highlight ? '#fbbf24' : '#e5e7eb',
          fontWeight: highlight ? 600 : 400,
          marginLeft: 12,
          textAlign: 'right',
          wordBreak: 'break-all',
        }}
      >
        {value === null || value === undefined ? '\u2014' : String(value)}
      </span>
    </div>
  );
}
